import { Card, Group, Stack, Table, Badge, TextInput, Loader, Drawer, Title, Text, Divider, Tabs, SimpleGrid, Avatar, Button, ActionIcon } from "@mantine/core";
import { IconPhone, IconMail, IconCake, IconPlus, IconEdit, IconTrash, IconSearch } from "@tabler/icons-react";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useResource, useOrders, useRemove, useInfiniteResource } from "../../../shared/api";
import { PageHeader, MoneyDisplay, EmptyState, KPICard, StatusBadge, formatDateTime, ResourceForm, confirmDelete, InfiniteTable, type FormField } from "../../../shared/ui";

const FIELDS: FormField[] = [
  { name: "phone", label: "Telefon", type: "phone", required: true, placeholder: "+90 555 123 45 67" },
  { name: "display_name", label: "İsim Soyisim" },
  { name: "email", label: "E-posta", type: "email" },
  { name: "birthday", label: "Doğum Tarihi", type: "date" },
  { name: "opt_in_marketing", label: "Pazarlama izni", type: "switch", defaultValue: false },
  { name: "opt_in_leaderboard", label: "Leaderboard izni", type: "switch", defaultValue: false },
];

export function CustomersPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [openId, setOpenId] = useState<string | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);
  const {
    items: list,
    total,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("customers", { search: debouncedSearch });
  const remove = useRemove("customers");

  function startCreate() {
    setEditing(null);
    open();
  }

  function startEdit(c: any, e: React.MouseEvent) {
    e.stopPropagation();
    setEditing({
      ...c,
      birthday: c.birthday || "",
    });
    open();
  }

  async function handleDelete(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    if (!(await confirmDelete("Müşteri silinsin mi? (GDPR — soft delete)"))) return;
    try {
      await remove.mutateAsync(id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (err: any) {
      notifications.show({ message: err?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Müşteriler"
        subtitle={total > 0 ? `${total} kayıtlı müşteri` : "Müşteri kayıtları"}
        actions={<Button leftSection={<IconPlus size={16} />} onClick={startCreate}>Yeni Müşteri</Button>}
      />
      <TextInput
        placeholder="Telefon, isim veya e-posta ile ara..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        size="md"
      />
      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={list}
          rowKey={(c) => c.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onRowClick={(c) => setOpenId(c.id)}
          emptyContent={
            <EmptyState
              title={debouncedSearch ? "Eşleşen müşteri yok" : "Müşteri yok"}
            />
          }
          columns={[
            { header: "Telefon", render: (c) => c.phone },
            { header: "İsim", render: (c) => c.display_name || "-" },
            { header: "Ziyaret", align: "right", render: (c) => c.total_visits },
            {
              header: "Toplam Harcama",
              align: "right",
              render: (c) => <MoneyDisplay minor={c.total_spent_minor} fw={600} />,
            },
            {
              header: "Son Ziyaret",
              render: (c) =>
                c.last_visit_at
                  ? new Date(c.last_visit_at).toLocaleDateString("tr-TR")
                  : "-",
            },
            {
              header: "Etiketler",
              render: (c) => (
                <Group gap="xs">
                  {c.opt_in_leaderboard && <Badge size="xs" color="yellow">Leaderboard</Badge>}
                  {c.opt_in_marketing && <Badge size="xs" color="blue">Marketing</Badge>}
                </Group>
              ),
            },
            {
              header: "",
              render: (c) => (
                <Group gap={4}>
                  <ActionIcon variant="subtle" onClick={(e) => startEdit(c, e)}><IconEdit size={16} /></ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={(e) => handleDelete(c.id, e)}><IconTrash size={16} /></ActionIcon>
                </Group>
              ),
            },
          ]}
        />
      </Card>

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="customers"
        title={editing ? "Müşteri Düzenle" : "Yeni Müşteri"}
        fields={FIELDS}
        editId={editing?.id || null}
        initialValues={editing || undefined}
        transform={(v) => ({ ...v, birthday: v.birthday || null })}
      />

      <Customer360Drawer customerId={openId} onClose={() => setOpenId(null)} />
    </Stack>
  );
}

function Customer360Drawer({ customerId, onClose }: { customerId: string | null; onClose: () => void }) {
  const { data: customer } = useResource<any>("customers", { ...(customerId ? { id: customerId } : {}), enabled: !!customerId });
  const { data: ordersData } = useOrders(customerId ? { customer: customerId } : undefined);
  const { data: badgesData } = useResource<any>("customer-badges", { filters: customerId ? { customer: customerId } : {}, enabled: !!customerId });

  const orders: any[] = ordersData?.results || ordersData || [];
  const badges: any[] = badgesData?.results || badgesData || [];

  return (
    <Drawer
      opened={!!customerId}
      onClose={onClose}
      position="right"
      size="xl"
      title={customer ? "Müşteri 360° Görünüm" : "Yükleniyor..."}
    >
      {!customer ? <Loader /> : (
        <Stack gap="md">
          <Card padding="lg" radius="md" withBorder>
            <Group>
              <Avatar size="xl" radius="xl" color="blue">
                {(customer.display_name || customer.phone || "?")[0]?.toUpperCase()}
              </Avatar>
              <Stack gap={4}>
                <Title order={4}>{customer.display_name || "İsimsiz"}</Title>
                <Group gap="xs">
                  <IconPhone size={14} />
                  <Text size="sm">{customer.phone}</Text>
                </Group>
                {customer.email && (
                  <Group gap="xs">
                    <IconMail size={14} />
                    <Text size="sm">{customer.email}</Text>
                  </Group>
                )}
                {customer.birthday && (
                  <Group gap="xs">
                    <IconCake size={14} />
                    <Text size="sm">{new Date(customer.birthday).toLocaleDateString("tr-TR")}</Text>
                  </Group>
                )}
              </Stack>
            </Group>
          </Card>

          <SimpleGrid cols={3}>
            <KPICard title="Toplam Ziyaret" value={customer.total_visits} color="blue" />
            <KPICard title="Toplam Harcama" value={<MoneyDisplay minor={customer.total_spent_minor} />} color="green" />
            <KPICard title="Rozet" value={badges.length} color="yellow" />
          </SimpleGrid>

          <Tabs defaultValue="orders">
            <Tabs.List>
              <Tabs.Tab value="orders">Siparişler ({orders.length})</Tabs.Tab>
              <Tabs.Tab value="badges">Rozetler ({badges.length})</Tabs.Tab>
              <Tabs.Tab value="info">Bilgi</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="orders" pt="md">
              {orders.length === 0 ? <EmptyState title="Sipariş yok" /> : (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Tarih</Table.Th>
                      <Table.Th>Masa</Table.Th>
                      <Table.Th>Durum</Table.Th>
                      <Table.Th ta="right">Tutar</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {orders.slice(0, 20).map((o: any) => (
                      <Table.Tr key={o.id}>
                        <Table.Td>{formatDateTime(o.created_at)}</Table.Td>
                        <Table.Td>{o.table_name || "-"}</Table.Td>
                        <Table.Td><StatusBadge status={o.kitchen_status} /></Table.Td>
                        <Table.Td ta="right"><MoneyDisplay minor={o.total_minor} fw={600} /></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="badges" pt="md">
              {badges.length === 0 ? <EmptyState title="Rozet yok" /> : (
                <SimpleGrid cols={3}>
                  {badges.map((b: any) => (
                    <Card key={b.id} padding="sm" radius="md" withBorder>
                      <Stack gap={4} align="center">
                        <Text size="2rem">🏅</Text>
                        <Text fw={600} size="sm">{b.badge_name}</Text>
                        <Text size="xs" c="dimmed">{formatDateTime(b.awarded_at)}</Text>
                      </Stack>
                    </Card>
                  ))}
                </SimpleGrid>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="info" pt="md">
              <Stack gap="xs">
                <Group justify="space-between"><Text c="dimmed">İlk Ziyaret:</Text><Text>{customer.first_visit_at ? formatDateTime(customer.first_visit_at) : "-"}</Text></Group>
                <Group justify="space-between"><Text c="dimmed">Son Ziyaret:</Text><Text>{customer.last_visit_at ? formatDateTime(customer.last_visit_at) : "-"}</Text></Group>
                <Group justify="space-between"><Text c="dimmed">Marketing Onayı:</Text><Badge color={customer.opt_in_marketing ? "green" : "gray"}>{customer.opt_in_marketing ? "Evet" : "Hayır"}</Badge></Group>
                <Group justify="space-between"><Text c="dimmed">Leaderboard Onayı:</Text><Badge color={customer.opt_in_leaderboard ? "green" : "gray"}>{customer.opt_in_leaderboard ? "Evet" : "Hayır"}</Badge></Group>
                {customer.social_links && Object.keys(customer.social_links).length > 0 && (
                  <>
                    <Divider />
                    <Text c="dimmed">Sosyal:</Text>
                    {Object.entries(customer.social_links).map(([k, v]: any) => (
                      <Group key={k} gap="xs"><Badge variant="light">{k}</Badge><Text size="sm">{v as string}</Text></Group>
                    ))}
                  </>
                )}
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      )}
    </Drawer>
  );
}
